import React, { useState, useEffect } from 'react';
import { useAccessibility } from '../../hooks/useAccessibility';

interface KeyboardShortcutsProps {
  isOpen: boolean;
  onClose: () => void;
}

export const KeyboardShortcuts: React.FC<KeyboardShortcutsProps> = ({ isOpen, onClose }) => {
  const { settings } = useAccessibility();
  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    const focusableElements = containerRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleTabKey);
    document.addEventListener('keydown', handleEscape);
    firstElement?.focus();

    return () => {
      document.removeEventListener('keydown', handleTabKey);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const shortcuts = [
    {
      category: 'Navigation',
      items: [
        { keys: ['Shift', '/'], description: 'Show keyboard shortcuts' },
        { keys: ['g', 'h'], description: 'Go to home' },
        { keys: ['g', 'd'], description: 'Go to dashboard' },
        { keys: ['g', 'p'], description: 'Go to profile' },
        { keys: ['g', 's'], description: 'Go to settings' },
        { keys: ['Escape'], description: 'Close dialog/modal' },
      ],
    },
    {
      category: 'Actions',
      items: [
        { keys: ['c'], description: 'Create new post' },
        { keys: ['n'], description: 'Open notifications' },
        { keys: ['m'], description: 'Open messages' },
        { keys: ['/'], description: 'Focus search' },
      ],
    },
    {
      category: 'Accessibility',
      items: [
        { keys: ['Ctrl', '+'], description: 'Increase font size' },
        { keys: ['Ctrl', '-'], description: 'Decrease font size' },
        { keys: ['Ctrl', '0'], description: 'Reset font size' },
        { keys: ['Alt', 'h'], description: 'Toggle high contrast' },
      ],
    },
    {
      category: 'Content',
      items: [
        { keys: ['j'], description: 'Next item' },
        { keys: ['k'], description: 'Previous item' },
        { keys: ['l'], description: 'Like/Unlike' },
        { keys: ['r'], description: 'Reply' },
        { keys: ['s'], description: 'Share' },
      ],
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="shortcuts-title"
    >
      <div
        ref={containerRef}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto m-4"
      >
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 id="shortcuts-title" className="text-2xl font-bold text-gray-900 dark:text-white">
            Keyboard Shortcuts
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded p-1"
            aria-label="Close keyboard shortcuts"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {shortcuts.map((section) => (
            <div key={section.category}>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                {section.category}
              </h3>
              <dl className="space-y-2">
                {section.items.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0"
                  >
                    <dt className="text-gray-700 dark:text-gray-300">{item.description}</dt>
                    <dd className="flex items-center gap-1">
                      {item.keys.map((key, keyIndex) => (
                        <React.Fragment key={keyIndex}>
                          {keyIndex > 0 && (
                            <span className="text-gray-500 mx-1">+</span>
                          )}
                          <kbd className="px-3 py-1 text-sm font-semibold text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-sm">
                            {key}
                          </kbd>
                        </React.Fragment>
                      ))}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>

        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {settings.keyboardShortcuts
              ? 'Keyboard shortcuts are enabled'
              : 'Keyboard shortcuts are disabled. Enable them in accessibility settings.'}
          </p>
        </div>
      </div>
    </div>
  );
};
